/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { TextField, IconButton, Box } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

interface ValidationRule {
  required?: boolean;
  maxLength?: number;
  min?: number;
}

interface InPlaceEditorProps {
  id?: string;
  name?: string;
  value?: string | number;
  type?: "Text" | "Numeric";
  mode?: "Inline";
  enableEditMode?: boolean;
  emptyText?: string;
  model?: {
    placeholder?: string;
    min?: number;
  };
  validationRules?: {
    Text?: ValidationRule;
    Number?: ValidationRule;
  };
  className?: string;
  // Callbacks
  actionSuccess?: (args: {
    value: string | number;
    previousValue: string | number;
    currentValue?: string | number;
  }) => void;
  actionBegin?: (args: {
    value: string | number;
    previousValue: string | number;
    currentValue?: string | number;
    cancel?: boolean;
  }) => void;
  cancelClick?: (args?: unknown) => void;
  endEdit?: () => void;
  created?: (args?: unknown) => void;
  beginEdit?: (args?: unknown) => void;
  change?: () => void;
}

export interface InPlaceEditorRef {
  validate: () => boolean;
  enableEditMode: boolean;
  properties?: {
    items?: unknown[];
  };
}

const InPlaceEditorComponent = forwardRef<InPlaceEditorRef, InPlaceEditorProps>((props, ref) => {
  const {
    id,
    name: _name,
    value: initialValue = "",
    type = "Text",
    enableEditMode: initialEditMode = false,
    model = {},
    validationRules = {},
    className,
    actionSuccess,
    actionBegin,
    cancelClick,
    endEdit,
    created,
    beginEdit,
    change,
  } = props;

  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [currentValue, setCurrentValue] = useState(initialValue);
  const [editValue, setEditValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const rules = type === "Numeric" ? validationRules.Number : validationRules.Text;

  useEffect(() => {
    if (created) {
      created();
    }
  }, []);

  useEffect(() => {
    setCurrentValue(initialValue);
    setEditValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (initialEditMode && !isEditing) {
      setIsEditing(true);
    }
  }, [initialEditMode]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const validate = (): boolean => {
    if (!rules) return true;

    const val = editValue;

    if (rules.required && (val === "" || val === null || val === undefined)) {
      setError("This field is required");
      return false;
    }

    if (rules.maxLength && typeof val === "string" && val.length > rules.maxLength) {
      setError(`Maximum ${rules.maxLength} characters allowed`);
      return false;
    }

    if (type === "Numeric" && rules.min !== undefined) {
      const numVal = typeof val === "string" ? parseFloat(val) : val;
      if (numVal < rules.min) {
        setError(`Minimum value is ${rules.min}`);
        return false;
      }
    }

    setError(null);
    return true;
  };

  useImperativeHandle(ref, () => ({
    validate,
    enableEditMode: isEditing,
    properties: {
      items: [],
    },
  }));

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditValue(currentValue);
    if (beginEdit) {
      beginEdit();
    }
  };

  const handleSave = () => {
    if (!validate()) return;

    const args = {
      value: editValue,
      previousValue: currentValue,
      cancel: false,
    };

    if (actionBegin) {
      actionBegin(args);
      if (args.cancel) return;
    }

    setCurrentValue(editValue);
    setIsEditing(false);
    setError(null);

    if (actionSuccess) {
      actionSuccess({ value: editValue, previousValue: currentValue });
    }

    if (endEdit) {
      endEdit();
    }
  };

  const handleCancel = () => {
    setEditValue(currentValue);
    setIsEditing(false);
    setError(null);

    if (cancelClick) {
      cancelClick();
    }

    if (endEdit) {
      endEdit();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = type === "Numeric" ? e.target.value : e.target.value;
    setEditValue(newValue);
    setError(null);
    if (change) {
      change();
    }
  };

  if (isEditing) {
    return (
      <Box
        id={id}
        className={className}
        sx={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}
        onClick={(e) => e.stopPropagation()}
      >
        <TextField
          inputRef={inputRef}
          value={editValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          type={type === "Numeric" ? "number" : "text"}
          placeholder={model.placeholder}
          size="small"
          error={!!error}
          helperText={error}
          slotProps={{
            input: {
              inputProps: {
                min: model.min ?? rules?.min,
              },
            },
          }}
          sx={{
            "& .MuiInputBase-input": {
              padding: "0.25rem 0.5rem",
              fontSize: "inherit",
            },
            "& .MuiOutlinedInput-root": {
              minHeight: "auto",
            },
          }}
        />
        <IconButton size="small" onClick={handleSave} color="primary" sx={{ padding: "0.125rem" }}>
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={handleCancel} sx={{ padding: "0.125rem" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      id={id}
      className={className}
      onClick={handleStartEdit}
      sx={{
        cursor: "pointer",
        display: "inline-block",
        padding: "0.125rem 0.25rem",
        borderRadius: "0.25rem",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      {currentValue || model.placeholder || "Click to edit"}
    </Box>
  );
});

InPlaceEditorComponent.displayName = "InPlaceEditorComponent";

export default InPlaceEditorComponent;
